import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { getFileExtension } from '@/utils/getFileExtension';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDownloadFileOnRemote } from '@/service/serverless-gpu/hook';
import { toastSomethingError } from '@/components/ui/toast-content';
import { StorageItem } from '..';
import { formatFileSize } from '@/utils/formatNumber';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';

interface DialogPreviewFileProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  file?: StorageItem;
}

const DialogPreviewFile = ({
  open: openOutside,
  onOpenChange,
  file,
  projectId,
}: DialogPreviewFileProps) => {
  const {
    name = '',
    fullPath = '',
    size = '',
    type = '',
    modified = '',
  } = file || {};
  const maxFileSizeInMB = 1;
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const fileExtension = getFileExtension(name);
  const fileSizeInMB = Number(size) / (1024 * 1024);
  const isLargeFile = fileSizeInMB > maxFileSizeInMB;
  const isTextFile = [
    '.txt',
    '.md',
    '.csv',
    '.log',
    '.py',
    '.js',
    '.ts',
    '.jsx',
    '.tsx',
    '.html',
    '.css',
    '.scss',
    '.php',
    '.java',
    '.cpp',
    '.c',
    '.go',
    '.json',
    '.yaml',
    '.yml',
    '.xml',
    '.conf',
    '.ini',
    '.bat',
    '.sh',
    '.bash',
    '.zsh',
    '.fish',
    '.powershell',
    '.env'
  ].includes(fileExtension);
  const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'].includes(
    fileExtension
  );
  const isPDF = fileExtension === '.pdf';
  const isSupportedFile = isTextFile || isImage || isPDF;
  const open = openOutside && (!isPDF || isLargeFile);

  const { mutateAsync: downloadFile } = useDownloadFileOnRemote({
    mutationKey: ['downloadFile', fullPath],
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: async (data) => {
      const fileUrl = data.downloads?.[0]?.url;
      if (isPDF) {
        window.open(fileUrl, '_blank');
        onOpenChange(false);
        setIsLoading(false);
        setNeedsConfirmation(false);
        setIsConfirmed(false);
        return;
      }

      if (isImage) {
        setContent(fileUrl);
        setNeedsConfirmation(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(fileUrl, {
          responseType: 'text',
        });

        setContent(response.data);
        setNeedsConfirmation(false);
      } catch (error) {
        console.error('Error previewing file:', error);
        toast.error('Failed to preview file');
        onOpenChange(false);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (fullPath && open) {
      if (isLargeFile && !isConfirmed) {
        setNeedsConfirmation(true);
        return;
      }

      downloadFile({
        project_id: projectId!,
        file_path: [fullPath],
      });
    }
  }, [fullPath, projectId, isConfirmed, open]);

  useEffect(() => {
    if (open === false) {
    //   setContent('');
      setIsLoading(false);
      setNeedsConfirmation(false);
      setIsConfirmed(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
          <div className="flex items-center justify-between">
            <div>
              <p>Size: {formatFileSize(Number(size))}</p>
              <p>Modified: {modified}</p>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] w-full rounded-md border p-4">
          {!isSupportedFile ? (
            <div className="flex h-[200px] items-center justify-center">
              <div className="text-muted-foreground">Not supported preview</div>
            </div>
          ) : isLoading ? (
            <div className="flex h-[200px] items-center justify-center">
              <div className="text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            </div>
          ) : needsConfirmation ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <p className="text-center text-muted-foreground">
                This file is larger than {maxFileSizeInMB}mb. Are you sure you
                want to load it?
              </p>
              <Button onClick={() => setIsConfirmed(true)}>Confirm Load</Button>
            </div>
          ) : isImage ? (
            <div className="flex items-center justify-center">
              <img
                src={content}
                alt={name}
                className="max-h-[50vh] max-w-full object-contain"
              />
            </div>
          ) : isTextFile ? (
            <SyntaxHighlighter language={fileExtension.slice(1)} showCopy>
              {content}
            </SyntaxHighlighter>
          ) : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default DialogPreviewFile;
